import { computed, inject, provide, type ComputedRef, type InjectionKey } from 'vue';
import { quickSearchTerms } from '@/utils/highlightMatch';
import { quickSearchTag } from '@/utils/quickSearchFields';

/**
 * Which fields each row of a quick-searched list matched (SAU-467), shared from the list
 * page down to its table cells and cards, so each can highlight its own value.
 *
 * The backend sends the matches in the page's `meta.search_matches`, keyed by record id:
 * `{"12": [{"field": "email", "value": "andre@example.com"}]}`.
 */

export interface QuickSearchMatch {
    field: string;
    value: string;
}

export interface QuickSearchMatchContext {
    /** The typed terms, split as the backend splits them. Empty when nothing is searched. */
    terms: ComputedRef<string[]>;
    /** The fields one record matched, in the order its record type lists them. */
    matchesFor(rowId: unknown): QuickSearchMatch[];
    /** Whether a field is matched only as a whole number, so its whole value is marked. */
    isNumberField(fieldKey: string): boolean;
}

const QUICK_SEARCH_MATCHES: InjectionKey<QuickSearchMatchContext> = Symbol('quickSearchMatches');

/** The record type's matches for the page, read from the search response's meta. */
export function provideQuickSearchMatches(options: {
    entityId: () => string;
    text: () => string;
    meta: () => unknown;
}): QuickSearchMatchContext {
    const matchesById = computed<Record<string, QuickSearchMatch[]>>(() => {
        const meta = options.meta() as { search_matches?: Record<string, QuickSearchMatch[]> } | null | undefined;
        return meta?.search_matches && typeof meta.search_matches === 'object' ? meta.search_matches : {};
    });
    const context: QuickSearchMatchContext = {
        terms: computed(() => quickSearchTerms(options.text())),
        matchesFor: (rowId) => (rowId === null || rowId === undefined ? [] : (matchesById.value[String(rowId)] ?? [])),
        isNumberField: (fieldKey) => quickSearchTag(options.entityId(), fieldKey)?.kind === 'number',
    };
    provide(QUICK_SEARCH_MATCHES, context);
    return context;
}

/** The page's matches, or null outside a quick-searched list. */
export function useQuickSearchMatches(): QuickSearchMatchContext | null {
    return inject(QUICK_SEARCH_MATCHES, null);
}

/**
 * The first match whose field the row does not show, for the one extra line under a row.
 * Null when every matched field is on screen, so its value is already highlighted there.
 */
export function hiddenMatch(matches: QuickSearchMatch[], shownFieldKeys: ReadonlySet<string>): QuickSearchMatch | null {
    return matches.find((match) => !shownFieldKeys.has(match.field)) ?? null;
}
